import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const DISMISSED_KEY = 'unclaimed_courses_banner_dismissed';

/**
 * Shows a banner notification when the user has unclaimed course enrollments.
 * Checks user.unclaimed_courses_count from /api/users/me/ response.
 * Dismissible per session (sessionStorage).
 */
export const UnclaimedCourseBanner: React.FC = () => {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  // Load dismissed state from sessionStorage
  useEffect(() => {
    const stored = sessionStorage.getItem(DISMISSED_KEY);
    if (stored === 'true') {
      setDismissed(true);
    }
  }, []);

  const dismiss = (): void => {
    setDismissed(true);
    sessionStorage.setItem(DISMISSED_KEY, 'true');
  };

  const count = user?.unclaimed_courses_count ?? 0;

  if (!user || count === 0 || dismissed) {
    return null;
  }

  return (
    <div className="bg-accent-yellow/10 border-b border-accent-yellow px-4 py-2 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <svg className="w-4 h-4 text-accent-yellow flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
        <span className="text-sm text-gray-300">
          {count === 1
            ? 'You have a new course available to claim.'
            : `You have ${count} new courses available to claim.`}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Link
          to="/courses"
          className="px-3 py-1 text-sm font-medium bg-accent-yellow hover:bg-accent-yellow text-dark-bg rounded transition-colors"
        >
          View Courses
        </Link>
        <button
          onClick={dismiss}
          className="text-gray-500 hover:text-gray-400 transition-colors"
          aria-label="Dismiss"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};
